import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Users, 
  FileText, 
  BarChart3, 
  Trash2, 
  Edit, 
  Shield,
  Download,
  RefreshCw,
  AlertTriangle,
  Building2,
  UserCircle,
  Briefcase,
  Copy,
  Check,
  Clock,
  Zap,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editPolicyDialog, setEditPolicyDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  
  // Edit form states
  const [editUserForm, setEditUserForm] = useState({});
  const [editPolicyForm, setEditPolicyForm] = useState({});
  
  // Copy state
  const [copied, setCopied] = useState(false);
  
  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [fetchingPolicies, setFetchingPolicies] = useState(false);

  const getToken = () => localStorage.getItem('polibot_token');

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      if (response.data.is_admin) {
        setIsAdmin(true);
        fetchAllData();
      } else {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Access denied');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    await Promise.all([fetchStats(), fetchUsers(), fetchPolicies(), fetchSchedulerStatus()]);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?limit=100`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/admin/policies?limit=100`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPolicies(response.data.policies);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/scheduler/status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    }
  };

  const triggerManualFetch = async () => {
    setFetchingPolicies(true);
    try {
      const response = await axios.post(`${API}/admin/scheduler/trigger-fetch`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.data.success) {
        toast.success(`Fetched ${response.data.imported_count} new policies!`);
        fetchPolicies();
        fetchSchedulerStatus();
        fetchStats();
      } else {
        toast.error(response.data.error || 'Fetch failed');
      }
    } catch (error) {
      toast.error('Failed to trigger fetch');
    } finally {
      setFetchingPolicies(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedItem(user);
    setEditUserForm({
      organization_name: user.organization_name || '',
      company_name: user.company_name || '',
      user_type: user.user_type || 'community_member',
      language: user.language || 'en',
      state: user.state || 'NY'
    });
    setEditUserDialog(true);
  };

  const handleEditPolicy = (policy) => {
    setSelectedItem(policy);
    setEditPolicyForm({
      title: policy.title || '',
      description: policy.description || '',
      category: policy.category || 'environmental',
      jurisdiction: policy.jurisdiction || 'federal',
      risk_score: policy.risk_score || 50,
      status: policy.status || 'active'
    });
    setEditPolicyDialog(true);
  };

  const handleDelete = (item, type) => {
    setSelectedItem(item);
    setDeleteType(type);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const endpoint = deleteType === 'user' 
        ? `${API}/admin/users/${selectedItem.id}`
        : `${API}/admin/policies/${selectedItem.id}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success(`${deleteType === 'user' ? 'User' : 'Policy'} deleted successfully`);
      setDeleteDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const saveUserChanges = async () => {
    try {
      await axios.put(`${API}/admin/users/${selectedItem.id}`, editUserForm, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('User updated successfully');
      setEditUserDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const savePolicyChanges = async () => {
    try {
      await axios.put(`${API}/admin/policies/${selectedItem.id}`, editPolicyForm, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Policy updated successfully');
      setEditPolicyDialog(false);
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    }
  };

  const copyEmailsToClipboard = () => {
    const emails = users.map(u => u.email).join('\n');
    navigator.clipboard.writeText(emails);
    setCopied(true);
    toast.success('Emails copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getUserTypeIcon = (type) => {
    switch (type) {
      case 'community_org':
        return <Building2 className="h-4 w-4" />;
      case 'corporate_esg':
        return <Briefcase className="h-4 w-4" />;
      default:
        return <UserCircle className="h-4 w-4" />;
    }
  };

  const getUserTypeBadge = (type) => {
    const variants = {
      community_member: { label: 'Member', variant: 'secondary' },
      community_org: { label: 'Organization', variant: 'default' },
      corporate_esg: { label: 'Corporate', variant: 'outline' }
    };
    const config = variants[type] || variants.community_member;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getRiskBadge = (score) => {
    if (score >= 70) return <Badge variant="destructive">High ({score})</Badge>;
    if (score >= 40) return <Badge variant="warning" className="bg-amber-500">Medium ({score})</Badge>;
    return <Badge variant="secondary">Low ({score})</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary">Checking admin access...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                <p className="text-sm text-muted-foreground">Manage users, policies, and system settings</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchAllData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="h-4 w-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="policies" data-testid="tab-policies">
              <FileText className="h-4 w-4 mr-2" />
              Policies ({policies.length})
            </TabsTrigger>
            <TabsTrigger value="scheduler" data-testid="tab-scheduler">
              <Clock className="h-4 w-4 mr-2" />
              Scheduler
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {stats && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{stats.users.total}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        +{stats.users.recent_signups_7d} this week
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Total Policies</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{stats.policies.total}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {stats.policies.federal} federal
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">High Risk Policies</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-destructive">{stats.policies.high_risk}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Requires attention
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Community Profiles</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{stats.community.profiles}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {stats.community.connections} connections
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* User Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>User Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <UserCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <div className="text-2xl font-bold">{stats.users.community_members}</div>
                        <p className="text-sm text-muted-foreground">Community Members</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <Building2 className="h-8 w-8 mx-auto mb-2 text-primary" />
                        <div className="text-2xl font-bold">{stats.users.community_orgs}</div>
                        <p className="text-sm text-muted-foreground">Organizations</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <Briefcase className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                        <div className="text-2xl font-bold">{stats.users.corporate_users}</div>
                        <p className="text-sm text-muted-foreground">Corporate Users</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>View and manage all registered users</CardDescription>
                  </div>
                  <Button variant="outline" onClick={copyEmailsToClipboard} data-testid="copy-emails-btn">
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? 'Copied!' : 'Copy All Emails'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Signed Up</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.email}</TableCell>
                          <TableCell>{getUserTypeBadge(user.user_type)}</TableCell>
                          <TableCell>{user.organization_name || user.company_name || '-'}</TableCell>
                          <TableCell>
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(user, 'user')}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies">
            <Card>
              <CardHeader>
                <CardTitle>Policy Management</CardTitle>
                <CardDescription>View and manage all tracked policies</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Jurisdiction</TableHead>
                        <TableHead>Risk</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {policies.map((policy) => (
                        <TableRow key={policy.id}>
                          <TableCell className="font-medium max-w-xs truncate">
                            {policy.title}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{policy.category}</Badge>
                          </TableCell>
                          <TableCell>{policy.jurisdiction}</TableCell>
                          <TableCell>{getRiskBadge(policy.risk_score)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{policy.source || 'local'}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditPolicy(policy)}
                              data-testid={`edit-policy-${policy.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(policy, 'policy')}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-policy-${policy.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduler Tab */}
          <TabsContent value="scheduler">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-primary" />
                        Automatic Policy Fetching
                      </CardTitle>
                      <CardDescription>
                        Federal Register policies are automatically fetched at 6:00 AM and 6:00 PM EST daily
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={triggerManualFetch} 
                      disabled={fetchingPolicies}
                      data-testid="manual-fetch-btn"
                    >
                      {fetchingPolicies ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Fetching...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 mr-2" />
                          Fetch Now
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <Clock className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <p className="font-semibold">6:00 AM EST</p>
                      <p className="text-sm text-muted-foreground">Morning Fetch</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <Clock className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <p className="font-semibold">6:00 PM EST</p>
                      <p className="text-sm text-muted-foreground">Evening Fetch</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <Calendar className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                      <p className="font-semibold">Every 12 Hours</p>
                      <p className="text-sm text-muted-foreground">Automatic Updates</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {schedulerStatus && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Fetch Logs</CardTitle>
                    <CardDescription>Last 10 policy fetch operations</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Timestamp</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Imported</TableHead>
                            <TableHead>Skipped</TableHead>
                            <TableHead>Total Fetched</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {schedulerStatus.recent_logs.map((log, index) => (
                            <TableRow key={index}>
                              <TableCell className="text-sm">
                                {new Date(log.timestamp).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <Badge variant={log.status === 'success' ? 'default' : 'destructive'}>
                                  {log.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium text-emerald-600">
                                +{log.imported_count}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {log.skipped_count}
                              </TableCell>
                              <TableCell>{log.total_fetched}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information for {selectedItem?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>User Type</Label>
              <Select
                value={editUserForm.user_type}
                onValueChange={(v) => setEditUserForm({...editUserForm, user_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="community_member">Community Member</SelectItem>
                  <SelectItem value="community_org">Community Organization</SelectItem>
                  <SelectItem value="corporate_esg">Corporate ESG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input
                value={editUserForm.organization_name}
                onChange={(e) => setEditUserForm({...editUserForm, organization_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Select
                value={editUserForm.state}
                onValueChange={(v) => setEditUserForm({...editUserForm, state: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NY">New York</SelectItem>
                  <SelectItem value="CA">California</SelectItem>
                  <SelectItem value="TX">Texas</SelectItem>
                  <SelectItem value="LA">Louisiana</SelectItem>
                  <SelectItem value="MI">Michigan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)}>Cancel</Button>
            <Button onClick={saveUserChanges}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Policy Dialog */}
      <Dialog open={editPolicyDialog} onOpenChange={setEditPolicyDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Policy</DialogTitle>
            <DialogDescription>
              Update policy information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editPolicyForm.title}
                onChange={(e) => setEditPolicyForm({...editPolicyForm, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editPolicyForm.description}
                onChange={(e) => setEditPolicyForm({...editPolicyForm, description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={editPolicyForm.category}
                  onValueChange={(v) => setEditPolicyForm({...editPolicyForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="environmental">Environmental</SelectItem>
                    <SelectItem value="health">Health</SelectItem>
                    <SelectItem value="housing">Housing</SelectItem>
                    <SelectItem value="labor">Labor</SelectItem>
                    <SelectItem value="voting">Voting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Jurisdiction</Label>
                <Select
                  value={editPolicyForm.jurisdiction}
                  onValueChange={(v) => setEditPolicyForm({...editPolicyForm, jurisdiction: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="federal">Federal</SelectItem>
                    <SelectItem value="NY">New York</SelectItem>
                    <SelectItem value="CA">California</SelectItem>
                    <SelectItem value="local">Local</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Risk Score (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={editPolicyForm.risk_score}
                  onChange={(e) => setEditPolicyForm({...editPolicyForm, risk_score: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editPolicyForm.status}
                  onValueChange={(v) => setEditPolicyForm({...editPolicyForm, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPolicyDialog(false)}>Cancel</Button>
            <Button onClick={savePolicyChanges}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              {deleteType === 'user' 
                ? `Are you sure you want to delete user "${selectedItem?.email}"? This action cannot be undone.`
                : `Are you sure you want to delete policy "${selectedItem?.title}"? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
